/*
  Checkbox.jsx

  Custom checkbox, similar to MUI checkbox.
*/

// Npm.
import React, { useMemo } from 'react';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Icons.
import TickIcon from '../../icons/Done';

// Constants.
const NOOP = () => {};

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    display: 'inline-flex',
    position: 'relative',
    verticalAlign: 'middle',
    userSelect: 'none',
    overflow: 'hidden',
    '& > input': {
      position: 'absolute',
      opacity: 0,
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      margin: 0,
      padding: 0,
      zIndex: 1,
      cursor: 'pointer',
      '&:checked + .checkbox': {
        backgroundColor: theme.color_primary_main,
      },
      '&:focus-visible + .checkbox': {
        boxShadow: `0 0 0 2px ${theme.color_primary_main} inset`,
      },
      '&:focus-visible:checked + .checkbox': {
        boxShadow: 'none',
        color: theme.color_text_primary,
      },
      '&:disabled': {
        cursor: 'inherit',
        '& + .checkbox': {
          backgroundColor: theme.action_hover,
        },
      },
      '&:read-only': {
        cursor: 'inherit',
      },
    },
    '& > .checkbox': {
      width: 16,
      height: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      backgroundColor: theme.color_divider,
      borderRadius: '4px',
      transition: 'left 0.1s ease',
      color: theme.color_paper,
      '& > .SvgIcon': {
        width: 20,
        height: 20,
      }
    },
  },
}));

/**
 * Switch component.
 */
export default function Checkbox({

  // Main props.
  id,
  className,
  fullWidth = false,
  size,

  // Input props.
  defaultChecked,
  checked,
  disabled,
  readOnly,
  autoFocus,

  // Input callbacks.
  onChange,
  onFocus,
  onBlur,

  children,
  ...rest

}) {


  const rootClass = 'Checkbox';

  const classes = useStyles();
  const cls = useMemo(() => cx(rootClass, classes.root, className), [rootClass, classes.root, className]);

  return (
    <div className={cls} {...rest}>
      <input
        id={id}
        type="checkbox"
        defaultChecked={defaultChecked}
        checked={checked}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
        onChange={readOnly ? NOOP : onChange}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <div className="checkbox">
        {
          Boolean(checked) &&
          <TickIcon />
        }
      </div>
    </div>
  );
}
